"use client"

import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../../store/slices/authSlice";
import { signInWithEmailAndPassword, signInWithGoogle, isAdminUser } from "../../services/auth";
import toast from "react-hot-toast";
import NameConfirmationModal from "../../components/auth/NameConfirmationModal";
import type { User } from "firebase/auth";
import { Mail, Lock, Eye, EyeOff, Chrome } from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { userService } from "../../services/userService";
const Logo = "/Logo.png";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [tempUser, setTempUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Sign in, but do not throw on unverified email
      const user = await signInWithEmailAndPassword(email, password, { skipEmailCheck: true });
      let userData = await userService.getUserData(user.uid);

      // If user document doesn't exist, create it with admin role for admin emails
      if (!userData) {
        const isAdmin = isAdminUser(email);
        await userService.initializeUserData(user.uid, {
          name: user.displayName || email.split("@")[0],
          email: email,
          role: isAdmin ? "admin" : "user",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        userData = await userService.getUserData(user.uid);
      }

      dispatch(
        setUser({
          uid: user.uid,
          email: userData?.email || email,
          name: userData?.name || user.displayName || email.split("@")[0],
          role: "admin", // Ensure admin role is set
        })
      );
      toast.success("Logged in successfully as admin!");
      navigate(from, { replace: true });
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const user = await signInWithGoogle();
      const userData = await userService.getUserData(user.uid);

      if (userData) {
        dispatch(
          setUser({
            uid: user.uid,
            email: userData.email,
            name: userData.name,
            role: userData.role,
          })
        );
        toast.success("Logged in successfully!");
        navigate(from, { replace: true });
      } else {
        // If no user data exists, show name confirmation modal
        setTempUser(user);
        setShowNameModal(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameConfirm = async () => {
    if (tempUser) {
      try {
        const userData = await userService.getUserData(tempUser.uid);
        if (userData) {
          dispatch(
            setUser({
              uid: tempUser.uid,
              email: userData.email,
              name: userData.name,
              role: userData.role,
            })
          );
          toast.success("Logged in successfully!");
          navigate(from, { replace: true });
        } else {
          throw new Error("User data not found");
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error("An unexpected error occurred");
        }
      } finally {
        setShowNameModal(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
            <img src={Logo} alt="Anmol Furniture Bazar Logo" className="w-30 h-16 rounded-lg border-2 border-primary-600" />
            
          </Link>
          <h2 className="text-3xl font-bold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-gray-600">Sign in to your account</p>
        </div>
        <div className="bg-white py-8 px-6 shadow-lg rounded-xl">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : "Sign In"}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Chrome className="w-5 h-5 mr-2" />
              Sign in with Google
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link to="/signup" className="font-medium text-primary-600 hover:text-primary-700">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {showNameModal && tempUser && <NameConfirmationModal user={tempUser} onComplete={handleNameConfirm} />}
    </div>
  );
};

export default Signup;
